export class SoundPlayer {
  constructor(audioContext) {
    this.ctx =
      audioContext || new (window.AudioContext || window.webkitAudioContext)();

    // Optional global SFX volume control
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 1.0; // global SFX volume 0–1
    this.masterGain.connect(this.ctx.destination);
  }

  /**
   * Unlock the AudioContext (call once from a user gesture).
   */
  async unlock() {
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }
  }

  /**
   * Play a UI sound once at a given volume.
   *
   * @param {AudioBuffer} audioBuffer
   * @param {Object} [options]
   * @param {number} [options.volume=1.0]  // 0–1 per sound
   * @param {number} [options.playbackRate=1.0] // optional pitch/tempo tweak
   */
  playOnce(audioBuffer, options = {}) {
    if (!audioBuffer) {
      console.warn("UISoundPlayer.playOnce called without an AudioBuffer");
      return;
    }

    const { volume = 1.0, playbackRate = 1.0 } = options;

    const source = this.ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.playbackRate.value = playbackRate;
    source.loop = false;

    const gainNode = this.ctx.createGain();
    gainNode.gain.value = Math.max(0, Math.min(volume, 1));

    source.connect(gainNode).connect(this.masterGain);

    // Clean up after it finishes
    source.onended = () => {
      try {
        source.disconnect();
        gainNode.disconnect();
      } catch (_) {}
    };

    source.start(0);
  }

  /**
   * Set global SFX volume (0–1).
   */
  setMasterVolume(volume) {
    const v = Math.max(0, Math.min(volume, 1));
    this.masterGain.gain.value = v;
  }

  /**
   * Helper: decode an ArrayBuffer into an AudioBuffer for use with playOnce.
   *
   * @param {ArrayBuffer|TypedArray} arrayBuffer
   * @returns {Promise<AudioBuffer>}
   */
  async createAudioBufferFromArrayBuffer(arrayBuffer) {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }

    const buf =
      arrayBuffer instanceof ArrayBuffer ? arrayBuffer : arrayBuffer.buffer;

    // .slice(0) to ensure it's a standalone buffer
    return await this.ctx.decodeAudioData(buf.slice(0));
  }
}
